import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { QRCodeCanvas } from 'qrcode.react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FilterBar, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { SectionCard } from '../../../components/SectionCard'
import { IStatusConfig, StatusBadge, statusOptions } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { formatDateTime, useHasRole, useAppToast } from '../../../helpers'
import { MachineService } from '../../../services/Machine/MachineService'
import { Machine, MachineStatus, MachinesFilter } from '../../../services/Machine/types'

/**
 * Status da maquininha. Fica no módulo porque só existe aqui, mas no formato que o
 * `<StatusBadge>` lê — o rótulo estava num ternário, a cor noutro, e as opções do filtro eram uma
 * terceira cópia dos mesmos dois rótulos digitada à mão.
 */
const MACHINE_STATUS: Record<MachineStatus, IStatusConfig> = {
  active: { label: 'Ativo', colorScheme: 'green' },
  inactive: { label: 'Inativo', colorScheme: 'gray' }
}

export default function MachinesPage() {
  const toast = useAppToast()
  const hasRole = useHasRole()
  const service = useMemo(() => new MachineService(), [])

  const [machines, setMachines] = useState<Machine[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [totalPages, setTotalPages] = useState(1)

  const [filter, setFilter] = useState<MachinesFilter>({
    search: '',
    status: '',
    page: 1,
    page_size: DEFAULT_PAGE_SIZE
  })

  // Create machine modal
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [newMachineName, setNewMachineName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Re-auth modal
  const [reAuthTarget, setReAuthTarget] = useState<Machine | null>(null)
  const [reAuthing, setReAuthing] = useState(false)

  // QR / activation code modal
  const [activationCode, setActivationCode] = useState('')
  const [activationQrPayload, setActivationQrPayload] = useState('')
  const [activationExpiresAt, setActivationExpiresAt] = useState('')
  const [qrModalOpen, setQrModalOpen] = useState(false)

  // Edit name modal
  const [editTarget, setEditTarget] = useState<Machine | null>(null)
  const [editName, setEditName] = useState('')
  const [editing, setEditing] = useState(false)

  // Deactivate confirmation
  const [deactivateTarget, setDeactivateTarget] = useState<Machine | null>(null)
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false)

  const canManage = hasRole('manage_machines')

  const loadMachines = useCallback(() => {
    setLoading(true)
    setLoadError(false)
    service
      .list({ ...filter })
      .then((data) => {
        setMachines(data.data)
        setTotalPages(Math.ceil(data.total_filtered / data.per_page) || 1)
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar as maquininhas', error)
      })
      .finally(() => setLoading(false))
  }, [filter, service])

  useEffect(() => {
    loadMachines()
  }, [loadMachines])

  /* ── Create ─────────────────────────────────────── */
  const handleOpenCreate = () => {
    setNewMachineName('')
    setCreateError(null)
    setCreateModalOpen(true)
  }

  const handleCreate = () => {
    if (!newMachineName.trim()) return
    setCreating(true)
    setCreateError(null)
    service
      .create(newMachineName.trim())
      .then((data) => {
        setCreateModalOpen(false)
        setActivationCode(data.activation_code)
        setActivationQrPayload(data.qr_payload)
        setActivationExpiresAt(data.expires_at)
        setQrModalOpen(true)
      })
      .catch((err) => {
        const fieldErrors = err?.response?.data?.errors
        const message =
          (fieldErrors && (Object.values(fieldErrors).flat() as string[])[0]) ||
          err?.response?.data?.message ||
          'Não foi possível cadastrar a maquininha.'
        setCreateError(message)
      })
      .finally(() => setCreating(false))
  }

  /* ── Re-auth ─────────────────────────────────────── */
  const handleReAuth = (machine: Machine) => {
    setReAuthTarget(machine)
    setReAuthing(true)
    service
      .reauthenticate(machine.id)
      .then((data) => {
        setActivationCode(data.activation_code)
        setActivationQrPayload(data.qr_payload)
        setActivationExpiresAt(data.expires_at)
        setQrModalOpen(true)
      })
      .catch((err) => {
        const message = err?.response?.data?.error || 'Não foi possível gerar novo código.'
        toast.error(message)
      })
      .finally(() => {
        setReAuthing(false)
        setReAuthTarget(null)
      })
  }

  /* ── Edit name ───────────────────────────────────── */
  const handleOpenEdit = (machine: Machine) => {
    setEditTarget(machine)
    setEditName(machine.name)
  }

  const handleSaveEdit = () => {
    if (!editTarget || !editName.trim()) return
    setEditing(true)
    service
      .update(editTarget.id, editName.trim())
      .then(() => {
        toast.success('Nome atualizado')
        setEditTarget(null)
        loadMachines()
      })
      .catch((err) => {
        const message = err?.response?.data?.error || 'Não foi possível atualizar o nome.'
        toast.error(message)
      })
      .finally(() => setEditing(false))
  }

  /* ── Deactivate ──────────────────────────────────── */
  const handleDeactivateClick = (machine: Machine) => {
    setDeactivateTarget(machine)
    setConfirmDeactivateOpen(true)
  }

  const handleConfirmDeactivate = () => {
    if (!deactivateTarget) return
    service
      .deactivate(deactivateTarget.id)
      .then(() => {
        toast.success('Maquininha desativada')
        loadMachines()
      })
      .catch(() => {
        toast.error('Não foi possível desativar a maquininha')
      })
      .finally(() => {
        setConfirmDeactivateOpen(false)
        setDeactivateTarget(null)
      })
  }

  /* ── QR modal close ──────────────────────────────── */
  const handleCloseQrModal = () => {
    setQrModalOpen(false)
    setActivationCode('')
    setActivationQrPayload('')
    setActivationExpiresAt('')
    loadMachines()
  }

  /* ── Filter helpers ──────────────────────────────── */
  const handleSearchChange = (value: string) => {
    setFilter((prev) => ({ ...prev, search: value, page: 1 }))
  }

  const handleStatusChange = (value: string) => {
    setFilter((prev) => ({ ...prev, status: value, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }))
  }

  return (
    <>
      <PageTitle title="Maquininhas" description="Terminais globais de check-in/check-out" mb={6} />

      <FilterBar
        onClear={() => setFilter((prev) => ({ ...prev, search: '', status: '', page: 1 }))}
        isDirty={filter.search !== '' || filter.status !== ''}
      >
        <FilterSearch
          label="Buscar por nome"
          value={filter.search}
          onChange={handleSearchChange}
          placeholder="Terminal 01..."
        />
        <FilterSelect
          label="Status"
          value={filter.status}
          onChange={handleStatusChange}
          options={statusOptions(MACHINE_STATUS)}
        />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Nome</Th>
              <Th>Status</Th>
              <Th>Última auth em</Th>
              <Th>
                Token expira em{' '}
                <Tooltip label="Validade do token legacy (X-Machine-Token). O app renova a sessão automaticamente via POST /refresh enquanto o refresh_token estiver válido (365 dias para terminal / 90 dias para tablet ou phone).">
                  <Icon name="lni lni-information" cursor="help" color="blue.300" size="md" />
                </Tooltip>
              </Th>
              <Th>Cadastrada por</Th>
              {canManage && <Th>Ações</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={canManage ? 6 : 5} />
            ) : loadError ? (
              <TableErrorRow colSpan={canManage ? 6 : 5} entity="maquininhas" feminine onRetry={loadMachines} />
            ) : machines.length === 0 ? (
              <TableEmptyRow colSpan={canManage ? 6 : 5} entity="maquininha" feminine />
            ) : (
              machines.map((machine) => (
                <Tr key={machine.id}>
                  <Td>{machine.name}</Td>
                  <Td>
                    <StatusBadge status={machine.status} statuses={MACHINE_STATUS} />
                  </Td>
                  <Td>{machine.last_authenticated_at ? formatDateTime(machine.last_authenticated_at) : '-'}</Td>
                  <Td>{machine.session_expires_at ? formatDateTime(machine.session_expires_at) : '-'}</Td>
                  <Td>{machine.created_by?.name ?? '-'}</Td>
                  {canManage && (
                    <Td>
                      <ActionButton
                        action="refresh"
                        label="Re-autenticar"
                        isLoading={reAuthTarget?.id === machine.id && reAuthing}
                        onClick={() => handleReAuth(machine)}
                      />
                      <ActionButton action="edit" label="Editar nome" onClick={() => handleOpenEdit(machine)} />
                      {machine.status === 'active' && (
                        <ActionButton
                          action="lock"
                          label="Desativar"
                          colorScheme="red"
                          icon={<Icon name="lni lni-close" />}
                          onClick={() => handleDeactivateClick(machine)}
                        />
                      )}
                    </Td>
                  )}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={filter.page}
        totalPages={totalPages}
        changePage={handlePageChange}
        pageSize={filter.page_size}
        onPageSizeChange={(size) => {
          setFilter({ ...filter, page_size: size, page: 1 })
        }}
      />

      {/* ── Create Machine Modal ──────────────────────── */}
      <FormModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Cadastrar nova maquininha"
        onSubmit={handleCreate}
        size="compact"
        confirmAction="create"
        confirmLabel="Cadastrar"
        isConfirmDisabled={!newMachineName.trim()}
        isLoading={creating}
      >
        <FormField label="Nome / Label da maquininha" error={createError}>
          <Input
            placeholder="Ex: Terminal 01"
            value={newMachineName}
            onChange={(e) => {
              setNewMachineName(e.target.value)
              if (createError) setCreateError(null)
            }}
            autoFocus
          />
        </FormField>
      </FormModal>

      {/* ── Activation Code / QR Modal ───────────────── */}
      <FormModal isOpen={qrModalOpen} onClose={handleCloseQrModal} title="Parear maquininha" size="compact">
        <Alert status="info" borderRadius="lg" mb={4} fontSize="xs" textAlign="left">
          <AlertIcon boxSize={4} />
          <AlertDescription>
            Este código autentica <b>apenas maquininhas (POS)</b>. Tablets de leilão devem ser autenticados pelo card do
            evento.
          </AlertDescription>
        </Alert>
        <Text variant="caption" mb={3}>
          Digite este código na maquininha ou escaneie o QR Code
        </Text>
        <Box bg="gray.50" borderRadius="xl" border="1px" borderColor="gray.200" py={5} px={4} mb={4}>
          <Text fontFamily="mono" fontSize="5xl" fontWeight="800" letterSpacing="0.3em" color="gray.800" lineHeight="1">
            {activationCode}
          </Text>
        </Box>
        <SectionCard variant="tile" display="inline-block" mb={3}>
          <QRCodeCanvas value={activationQrPayload || activationCode} size={140} includeMargin />
        </SectionCard>
        <Text variant="caption" mb={1}>
          Dispositivos com câmera podem escanear o QR Code
        </Text>
        <Text mt={2} fontSize="xs" color="orange.500" fontWeight="500">
          Código válido por 10 minutos.
        </Text>
        {activationExpiresAt && (
          <Text variant="caption" mt={1}>
            Expira em: {formatDateTime(activationExpiresAt)}
          </Text>
        )}
      </FormModal>

      {/* ── Edit Name Modal ───────────────────────────── */}
      <FormModal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        title="Editar nome da maquininha"
        onSubmit={handleSaveEdit}
        size="compact"
        isConfirmDisabled={!editName.trim()}
        isLoading={editing}
      >
        <FormField label="Novo nome">
          <Input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
        </FormField>
      </FormModal>

      {/* ── Deactivate Confirm Modal ──────────────────── */}
      <ConfirmDialog
        isOpen={confirmDeactivateOpen}
        onClose={() => {
          setConfirmDeactivateOpen(false)
          setDeactivateTarget(null)
        }}
        onConfirm={handleConfirmDeactivate}
        action="lock"
        title="Desativar maquininha"
        description={`Deseja realmente desativar "${deactivateTarget?.name}"? O token vigente passará a ser rejeitado.`}
        confirmLabel="Desativar"
      />

      {canManage && <ActionFab label="Cadastrar nova maquininha" onClick={handleOpenCreate} />}
    </>
  )
}
